import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Check, type LucideIcon } from "lucide-react";

import { Section } from "@/components/Section";
import { Container } from "@/components/Container";
import { Eyebrow } from "@/components/Eyebrow";
import { Reveal } from "@/components/Reveal";
import { Aurora } from "@/components/Aurora";
import { CtaButton } from "@/components/CtaButton";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Results } from "@/components/home/Results";
import { AREA_SERVED, NAP, SHOW_PHONE } from "@/lib/business";
import { SITE_URL, canonicalUrl } from "@/lib/seo";
import type { CaseStudy, Testimonial } from "@/lib/content";

export interface ServiceCard {
  icon: LucideIcon;
  title: string;
  body: string;
}

export interface ServiceStep {
  n: string;
  title: string;
  body: string;
}

export interface ServiceFaq {
  q: string;
  a: string;
}

export interface ServiceUseCases {
  heading: string;
  intro: string;
  groups: readonly {
    area: string;
    examples: readonly string[];
  }[];
}

export interface ServiceReadiness {
  heading: string;
  ready: readonly string[];
  notYet: readonly string[];
}

export interface ServicePageContent {
  /** Path beginning with "/" — used for breadcrumbs and schema ids. */
  path: string;
  /** Breadcrumb leaf and Service schema name. */
  name: string;
  eyebrow: string;
  h1: React.ReactNode;
  lead: string;
  problem: { heading: string; paragraphs: readonly string[] };
  useCases?: ServiceUseCases;
  cards: readonly ServiceCard[];
  deliverables: { heading: string; intro: string; items: readonly string[] };
  readiness?: ServiceReadiness;
  process: { heading: string; steps: readonly ServiceStep[] };
  timeline?: { heading: string; body: string };
  pricing: { heading: string; body: string };
  /** In-copy pointers to the scorecard and the two closest services. */
  related?: { heading: string; body: React.ReactNode };
  faqs: readonly ServiceFaq[];
  cta: { heading: string; line: string };
}

/** Service + BreadcrumbList + FAQPage JSON-LD for a service page. */
export function serviceJsonLd(content: {
  path: string;
  name: string;
  description: string;
  serviceType?: string;
  faqs: readonly ServiceFaq[];
}) {
  const url = canonicalUrl(content.path);
  return [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${url}#service`,
      name: content.name,
      description: content.description,
      url,
      serviceType: content.serviceType ?? content.name,
      areaServed: AREA_SERVED,
      provider: { "@id": `${SITE_URL}/#organization` },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${url}#breadcrumbs`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: content.name, item: url },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${url}#faqpage`,
      url,
      mainEntity: content.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.q,
        acceptedAnswer: { "@type": "Answer", text: faq.a },
      })),
    },
  ];
}

export function ServicePageLayout({
  content,
  caseStudies = [],
  testimonials = [],
}: {
  content: ServicePageContent;
  caseStudies?: CaseStudy[];
  testimonials?: Testimonial[];
}) {
  const {
    name,
    eyebrow,
    h1,
    lead,
    problem,
    useCases,
    cards,
    deliverables,
    readiness,
    process,
    timeline,
    pricing,
    related,
    faqs,
    cta,
  } = content;

  return (
    <>
      {/* 1 — Hero */}
      <Section tone="paper" className="relative overflow-hidden">
        <Aurora variant="light" className="opacity-70" />
        <Container className="relative">
          <nav aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-eyebrow uppercase text-ink-500">
              <li>
                <Link
                  to="/"
                  className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-sm text-brand-700 transition-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2"
                >
                  Home
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li aria-current="page" className="text-ink-500">
                {name}
              </li>
            </ol>
          </nav>

          <div className="mt-8">
            <Eyebrow>{eyebrow}</Eyebrow>
            <h1
              className="mt-5 max-w-[20ch] font-display text-display-lg text-ink-900"
              style={{ textWrap: "balance" }}
            >
              {h1}
            </h1>
          </div>

          <p className="mt-6 max-w-[60ch] text-body-lg text-ink-600">{lead}</p>

          <div className="mt-8">
            <CtaButton asChild>
              <Link to="/book">Book a Free AI Call</Link>
            </CtaButton>
          </div>
        </Container>
      </Section>

      {/* 2 — Problem framing */}
      <Section tone="raised">
        <Container variant="narrow">
          <Reveal>
            <h2 className="font-display text-h2 text-ink-900" style={{ textWrap: "balance" }}>
              {problem.heading}
            </h2>
            <div className="mt-6 space-y-5">
              {problem.paragraphs.map((paragraph) => (
                <p key={paragraph} className="max-w-[68ch] text-body-lg text-ink-600">
                  {paragraph}
                </p>
              ))}
            </div>
          </Reveal>
        </Container>
      </Section>

      {/* 3 — Use cases */}
      {useCases ? (
        <Section tone="paper">
          <Container>
            <Reveal className="max-w-[58ch]">
              <Eyebrow>Use cases</Eyebrow>
              <h2 className="mt-3 font-display text-h2 text-ink-900">{useCases.heading}</h2>
              <p className="mt-4 text-body-lg text-ink-600">{useCases.intro}</p>
            </Reveal>

            <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {useCases.groups.map((group, i) => (
                <Reveal key={group.area} delay={(i % 3) * 60} className="h-full">
                  <article className="h-full rounded-2xl border border-ink-200 bg-paper-0 p-6">
                    <h3 className="font-display text-h3 text-ink-900">{group.area}</h3>
                    <ul className="mt-5 space-y-3">
                      {group.examples.map((example) => (
                        <li key={example} className="flex gap-3 text-body text-ink-600">
                          <span
                            className="mt-2 size-1.5 shrink-0 rounded-full bg-brand-600"
                            aria-hidden="true"
                          />
                          <span>{example}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                </Reveal>
              ))}
            </div>
          </Container>
        </Section>
      ) : null}

      {/* 4 — What you get */}
      <Section tone="raised">
        <Container>
          <Reveal className="max-w-[52ch]">
            <Eyebrow>What you get</Eyebrow>
            <h2 className="mt-3 font-display text-h2 text-ink-900">Inside the engagement.</h2>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {cards.map((card, i) => {
              const Icon = card.icon;
              return (
                <Reveal key={card.title} delay={(i % 3) * 60} className="h-full">
                  <div className="flex h-full flex-col rounded-2xl border border-ink-200 bg-paper-0 p-6 shadow-[0_1px_2px_oklch(0.1831_0.0309_263.38/0.05)] transition-brand hover:border-brand-600/40 md:p-8">
                    <span
                      className="flex size-10 items-center justify-center rounded-md bg-brand-50"
                      aria-hidden="true"
                    >
                      <Icon size={20} strokeWidth={1.5} className="text-brand-700" />
                    </span>
                    <h3 className="mt-5 font-display text-h3 text-ink-900">{card.title}</h3>
                    <p className="mt-3 text-body text-ink-600">{card.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </Section>

      {/* 5 — Deliverables */}
      <Section tone="paper">
        <Container>
          <Reveal className="max-w-[52ch]">
            <Eyebrow>Deliverables</Eyebrow>
            <h2 className="mt-3 font-display text-h2 text-ink-900">{deliverables.heading}</h2>
            <p className="mt-4 text-body-lg text-ink-600">{deliverables.intro}</p>
          </Reveal>

          <Reveal className="mt-10">
            <ul className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {deliverables.items.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 rounded-2xl border border-ink-200 bg-paper-0 p-5 shadow-[0_1px_2px_oklch(0.1831_0.0309_263.38/0.05)]"
                >
                  <span
                    className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-50"
                    aria-hidden="true"
                  >
                    <Check size={16} strokeWidth={1.5} className="text-brand-700" />
                  </span>
                  <span className="text-body text-ink-600">{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </Container>
      </Section>

      {/* 6 — Readiness */}
      {readiness ? (
        <Section tone="raised">
          <Container>
            <Reveal className="max-w-[58ch]">
              <Eyebrow>Readiness</Eyebrow>
              <h2 className="mt-3 font-display text-h2 text-ink-900">{readiness.heading}</h2>
            </Reveal>

            <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <Reveal className="h-full">
                <div className="h-full rounded-2xl border border-ink-200 bg-paper-0 p-6 md:p-8">
                  <h3 className="font-display text-h3 text-ink-900">You are probably ready if…</h3>
                  <ul className="mt-5 space-y-4">
                    {readiness.ready.map((item) => (
                      <li key={item} className="flex gap-3 text-body text-ink-600">
                        <Check
                          size={18}
                          strokeWidth={1.5}
                          className="mt-0.5 shrink-0 text-brand-700"
                          aria-hidden="true"
                        />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>

              <Reveal className="h-full" delay={80}>
                <div className="h-full rounded-2xl border border-ink-200 bg-paper-0 p-6 md:p-8">
                  <h3 className="font-display text-h3 text-ink-900">
                    You are probably not ready yet if…
                  </h3>
                  <ul className="mt-5 space-y-4">
                    {readiness.notYet.map((item) => (
                      <li key={item} className="flex gap-3 text-body text-ink-600">
                        <span
                          className="mt-2 size-1.5 shrink-0 rounded-full bg-ink-500"
                          aria-hidden="true"
                        />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </Container>
        </Section>
      ) : null}

      {/* 7 — Process */}
      <Section tone="paper">
        <Container>
          <Reveal className="max-w-[52ch]">
            <Eyebrow>Process</Eyebrow>
            <h2 className="mt-3 font-display text-h2 text-ink-900">{process.heading}</h2>
          </Reveal>

          <ol className="mt-12 space-y-8">
            {process.steps.map((step, i) => (
              <Reveal key={step.n} delay={(i % 3) * 60}>
                <li className="flex gap-5">
                  <span
                    className="flex size-12 shrink-0 items-center justify-center rounded-full border-2 border-brand-600 bg-paper-0 font-mono text-body font-semibold text-brand-700"
                    aria-hidden="true"
                  >
                    {step.n}
                  </span>
                  <div className="pt-1">
                    <h3 className="font-display text-h3 text-ink-900">{step.title}</h3>
                    <p className="mt-2 max-w-[68ch] text-body text-ink-600">{step.body}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
        </Container>
      </Section>

      {/* 8 — Timeline */}
      {timeline ? (
        <Section tone="raised">
          <Container variant="narrow">
            <Reveal>
              <Eyebrow>Timeline</Eyebrow>
              <h2 className="mt-3 font-display text-h2 text-ink-900">{timeline.heading}</h2>
              <p className="mt-4 max-w-[68ch] text-body-lg text-ink-600">{timeline.body}</p>
            </Reveal>
          </Container>
        </Section>
      ) : null}

      {/* 9 — Proof (hides entirely when empty) */}
      <Results caseStudies={caseStudies} testimonials={testimonials} />

      {/* 10 — Pricing */}
      <Section tone="paper">
        <Container variant="narrow">
          <Reveal>
            <Eyebrow>Pricing</Eyebrow>
            <h2 className="mt-3 font-display text-h2 text-ink-900">{pricing.heading}</h2>
            <p className="mt-4 max-w-[68ch] text-body-lg text-ink-600">{pricing.body}</p>
          </Reveal>

          {/* 10b — Where this sits next to the rest of the work */}
          {related ? (
            <Reveal className="mt-14 border-t border-ink-200 pt-12">
              <h2 className="font-display text-h2 text-ink-900">{related.heading}</h2>
              <div className="mt-4 max-w-[68ch] space-y-4 text-body-lg text-ink-600 [&_a]:text-brand-700 [&_a]:underline [&_a]:underline-offset-4">
                {related.body}
              </div>
            </Reveal>
          ) : null}
        </Container>
      </Section>

      {/* 11 — FAQ */}
      <Section tone="raised">
        <Container variant="narrow">
          <Reveal className="max-w-[52ch]">
            <Eyebrow>Questions</Eyebrow>
            <h2 className="mt-3 font-display text-h2 text-ink-900">What clients ask about this.</h2>
          </Reveal>

          <Reveal className="mt-10">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => (
                <AccordionItem key={faq.q} value={`item-${i + 1}`} className="border-ink-200">
                  <AccordionTrigger className="text-ink-900">{faq.q}</AccordionTrigger>
                  <AccordionContent className="text-ink-600">{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </Container>
      </Section>

      {/* 12 — CTA */}
      <Section tone="dark" className="relative overflow-hidden text-center">
        <Aurora variant="dark" />
        <Container variant="narrow" className="relative">
          <Reveal>
            <div className="mx-auto max-w-[52ch]">
              <Eyebrow className="text-brand-300">Next step</Eyebrow>
              <h2
                className="mt-3 font-display text-h2 text-paper-0"
                style={{ textWrap: "balance" }}
              >
                {cta.heading}
              </h2>
              <p className="mt-4 text-body-lg text-ondark-body">{cta.line}</p>
            </div>
            <div className="mt-8 flex flex-col items-center gap-4">
              <CtaButton asChild tone="dark">
                <Link to="/book">Book a Free AI Call</Link>
              </CtaButton>
              {SHOW_PHONE ? (
                <a
                  href={NAP.phoneHref}
                  className="inline-flex min-h-[44px] items-center rounded-sm font-mono text-body-sm text-ondark-muted underline-offset-4 transition-brand hover:text-paper-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-900"
                >
                  Or call {NAP.phone}
                </a>
              ) : null}
            </div>
          </Reveal>
        </Container>
      </Section>
    </>
  );
}

export default ServicePageLayout;
